import {
  ContainerHistoryRecord,
  ContainerHistoryRecords,
  usePaginatedContainerHistory,
} from "../../../../utils/hooks/inventory/containerHistory";
import {
  datetimeStringToHumanReadableDatetime,
  dateToDatetimeString,
} from "../../../../utils/valueFormatting";
import { UserIdPill } from "../../../common/UserPill";
import {
  COLORS,
  Input,
  InputLabel,
  NoWrapFlashPCBTable,
  Pill,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../../constants/Styles";
import usePart from "../../../../utils/hooks/commonly-stored/parts";
import {
  FiltersContainer,
  HistoryContainer,
  InputContainer,
  NoHistory,
  To,
} from "./ContainerHistoryStyles";
import React, { ReactNode, SyntheticEvent, useRef, useState } from "react";
import Button from "../../../common/Button";
import { BinLink, ContainerLink } from "../BarcodeLink";
import FormLine from "../../../common/FormLine";
import LoadingOverlay from "../../../LoadingOverlay";
import Pagination from "../../../Pagination";
import { ButtonTypes, ButtonVariants } from "../../../common/Button/constants";
import HorizontalScroll from "../../../common/HorizontalScroll";
import LoadingIcon from "../../../common/LoadingIcon";
import getBoardLink from "../../../../utils/links/boards";
import { useStringInput } from "../../../../utils/hooks/inputs";
import Checkbox from "../../../common/Checkbox";

function BeforeAndAfter({
  label,
  previousValue,
  value,
}: {
  label: ReactNode;
  previousValue: ReactNode;
  value: ReactNode;
}) {
  return (
    <div>
      <InputLabel>{label}</InputLabel>
      <div>
        {previousValue} <To>→</To> {value}
      </div>
    </div>
  );
}

function ShipmentLink({
  boardId,
  shipmentId,
}: {
  boardId: string;
  shipmentId: string;
}) {
  return (
    <a href={getBoardLink(boardId, true, shipmentId)} target={"_blank"}>
      {boardId}
    </a>
  );
}

function HistoryUpdate({ record }: { record: ContainerHistoryRecord }) {
  if (record.state === null) {
    return null;
  }
  if (record.previous_state === null) {
    return (
      <div>
        {record.state.bin_barcode !== null && (
          <div>
            <InputLabel>Bin</InputLabel>
            <div>
              <BinLink binBarcode={record.state.bin_barcode} />
            </div>
          </div>
        )}
        <div>
          <InputLabel>Quantity</InputLabel>
          <div>{record.state.last_counted_amount}</div>
        </div>
      </div>
    );
  }

  const previousAllocationsShipmentIds = new Set<string>(
    record.previous_state.allocations.map(
      (allocation) => allocation.shipment_id,
    ),
  );
  const allocationsAdded = record.state.allocations.filter(
    (allocation) => !previousAllocationsShipmentIds.has(allocation.shipment_id),
  );

  const currentAllocationsShipmentIds = new Set<string>(
    record.state.allocations.map((allocation) => allocation.shipment_id),
  );
  const allocationsRemoved = record.previous_state.allocations.filter(
    (allocation) => !currentAllocationsShipmentIds.has(allocation.shipment_id),
  );

  interface UpdatedAllocation {
    board_id: string;
    shipment_id: string;
    oldQuantity: number;
    newQuantity: number;
  }

  const changedAllocations: UpdatedAllocation[] = [];
  for (const allocation of record.state.allocations) {
    const previousAllocation = record.previous_state.allocations.find(
      (previousAllocation) => {
        return previousAllocation.shipment_id === allocation.shipment_id;
      },
    );
    if (
      previousAllocation &&
      previousAllocation.quantity_allocated !== allocation.quantity_allocated
    ) {
      changedAllocations.push({
        board_id: allocation.board_id,
        shipment_id: allocation.shipment_id,
        oldQuantity: previousAllocation.quantity_allocated,
        newQuantity: allocation.quantity_allocated,
      });
    }
  }

  const binChanged =
    record.previous_state.bin_barcode != record.state.bin_barcode;
  const quantityChanged =
    record.previous_state.last_counted_amount !=
    record.state.last_counted_amount;

  return (
    <div>
      {binChanged && (
        <BeforeAndAfter
          label={"Moved bins"}
          previousValue={
            record.previous_state.bin_barcode ? (
              <BinLink binBarcode={record.previous_state.bin_barcode} />
            ) : (
              "Floating"
            )
          }
          value={
            record.state.bin_barcode ? (
              <BinLink binBarcode={record.state.bin_barcode} />
            ) : (
              "Floating"
            )
          }
        />
      )}
      {quantityChanged && (
        <BeforeAndAfter
          label={"Quantity updated"}
          previousValue={record.previous_state.last_counted_amount.toLocaleString()}
          value={record.state.last_counted_amount.toLocaleString()}
        />
      )}
      {allocationsAdded.length > 0 &&
        allocationsAdded.map((allocationAdded) => (
          <BeforeAndAfter
            key={allocationAdded.shipment_id}
            label={
              <>
                Allocation added for{" "}
                <ShipmentLink
                  boardId={allocationAdded.board_id}
                  shipmentId={allocationAdded.shipment_id}
                />{" "}
                ({allocationAdded.shipment_id})
              </>
            }
            previousValue={0}
            value={allocationAdded.quantity_allocated.toLocaleString()}
          />
        ))}
      {allocationsRemoved.length > 0 &&
        allocationsRemoved.map((allocationRemoved) => (
          <BeforeAndAfter
            key={allocationRemoved.shipment_id}
            label={
              <>
                Allocation removed for{" "}
                <ShipmentLink
                  boardId={allocationRemoved.board_id}
                  shipmentId={allocationRemoved.shipment_id}
                />{" "}
                ({allocationRemoved.shipment_id})
              </>
            }
            previousValue={allocationRemoved.quantity_allocated.toLocaleString()}
            value={0}
          />
        ))}
      {changedAllocations.length > 0 &&
        changedAllocations.map((allocationUpdate) => (
          <BeforeAndAfter
            key={allocationUpdate.shipment_id}
            label={
              <>
                Allocation updated for{" "}
                <ShipmentLink
                  boardId={allocationUpdate.board_id}
                  shipmentId={allocationUpdate.shipment_id}
                />{" "}
                ({allocationUpdate.shipment_id})
              </>
            }
            previousValue={allocationUpdate.oldQuantity.toLocaleString()}
            value={allocationUpdate.newQuantity.toLocaleString()}
          />
        ))}
      {!binChanged &&
        !quantityChanged &&
        !allocationsAdded.length &&
        !allocationsRemoved.length &&
        !changedAllocations.length && (
          <div>
            <InputLabel>What was changed</InputLabel>
            <div>
              Quantity, bin, or allocation did not change in this update
            </div>
          </div>
        )}
    </div>
  );
}

function RecordRow({ record }: { record: ContainerHistoryRecord }) {
  const { part } = usePart(record.part_id);

  const containerBarcode =
    record.previous_state?.container_barcode || record.state?.container_barcode;

  return (
    <TableRow>
      <TableCell>
        {part === null ? <Pill width={"100%"} /> : part.name}
      </TableCell>
      <TableCell>
        <ContainerLink containerBarcode={containerBarcode} />
      </TableCell>
      <TableCell>
        {datetimeStringToHumanReadableDatetime(record.timestamp)}
      </TableCell>
      <TableCell>
        <UserIdPill userId={record.user_id} />
      </TableCell>
      <TableCell>
        {record.previous_state === null && record.state !== null && "Added"}
        {record.previous_state !== null && record.state === null && "Deleted"}
        {record.previous_state !== null && record.state !== null && "Updated"}
      </TableCell>
      <TableCell>
        <HistoryUpdate record={record} />
      </TableCell>
    </TableRow>
  );
}

export default function ContainerHistoryTable({
  containerHistory,
  children,
}: {
  containerHistory: ContainerHistoryRecords | null;
  children?: ReactNode;
}) {
  const tableRef = useRef<HTMLTableElement>(null);

  if (containerHistory === null) {
    return (
      <LoadingIcon
        text={"Loading history..."}
        centerHorizontally
        centerVertically
      />
    );
  }

  if (containerHistory.records.length === 0) {
    return <NoHistory>No history collected yet</NoHistory>;
  }

  return (
    <HistoryContainer>
      <HorizontalScroll childrenRef={tableRef}>
        <NoWrapFlashPCBTable ref={tableRef}>
          <thead>
            <TableHeaderRow>
              <TableHeaderCell>Part</TableHeaderCell>
              <TableHeaderCell>Container barcode</TableHeaderCell>
              <TableHeaderCell>Timestamp</TableHeaderCell>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Update</TableHeaderCell>
            </TableHeaderRow>
          </thead>
          <tbody>
            {containerHistory.records.map((record) => {
              return <RecordRow key={JSON.stringify(record)} record={record} />;
            })}
          </tbody>
        </NoWrapFlashPCBTable>
      </HorizontalScroll>
      {children}
    </HistoryContainer>
  );
}

export function AllContainerHistory() {
  const {
    containerHistory,
    setSearchTerm,
    loading,
    resetSearch,
    page,
    setPage,
    setStartDatetime,
    setEndDatetime,
    setFilterNew,
  } = usePaginatedContainerHistory();
  const [startDatetimeRange, setStartDatetimeRange] = useState<string>("");
  const [endDatetimeRange, setEndDatetimeRange] = useState<string>("");
  const [filterNewContainers, setFilterNewContainers] =
    useState<boolean>(false);

  const {
    value: searchValue,
    setValue: setSearchValue,
    reset: resetSearchValue,
    bind: bindSearchValue,
  } = useStringInput("");

  const handleSearch = (event: SyntheticEvent) => {
    event.preventDefault();
    const trimmedSearchValue = searchValue.trim();
    setSearchTerm(trimmedSearchValue);
    setSearchValue(trimmedSearchValue);
    setStartDatetime(startDatetimeRange);
    setEndDatetime(endDatetimeRange);
    setFilterNew(filterNewContainers);
    setPage(1);
  };

  const onChangeStartDate = (event: SyntheticEvent<HTMLInputElement>) => {
    const dateString = (event.target as HTMLInputElement).value;
    setStartDatetimeRange(dateString);
  };

  const onChangeEndDate = (event: SyntheticEvent<HTMLInputElement>) => {
    const dateString = (event.target as HTMLInputElement).value;
    setEndDatetimeRange(dateString);
  };

  const allSearchValuesAreEmpty =
    !searchValue && !startDatetimeRange && !endDatetimeRange;

  return (
    <HistoryContainer>
      <LoadingOverlay loading={loading} text={"Loading history records..."} />
      <FiltersContainer>
        <FormLine
          label={"Search by"}
          name={"Search term"}
          placeholder={"Search by barcode, mpn, or part id..."}
          bind={bindSearchValue}
        />
        <InputContainer>
          <InputLabel>Start date</InputLabel>
          <Input
            style={{ color: COLORS.secondary }}
            type="datetime-local"
            value={startDatetimeRange}
            onChange={onChangeStartDate}
            max={dateToDatetimeString(new Date())}
          />
        </InputContainer>
        <InputContainer>
          <InputLabel>End date</InputLabel>
          <Input
            style={{ color: COLORS.secondary }}
            type="datetime-local"
            value={endDatetimeRange}
            onChange={onChangeEndDate}
            max={dateToDatetimeString(new Date())}
          />
        </InputContainer>
        <InputContainer>
          <InputLabel>New containers only</InputLabel>
          <Checkbox
            checked={filterNewContainers}
            onChange={() => setFilterNewContainers(!filterNewContainers)}
          />
        </InputContainer>
        <Button
          label={"Search"}
          onClick={handleSearch}
          type={ButtonTypes.SUBMIT}
          disabled={loading || allSearchValuesAreEmpty}
        />
        <Button
          label={"Clear filter"}
          onClick={() => {
            resetSearch();
            resetSearchValue();
            setStartDatetime("");
            setStartDatetimeRange("");
            setEndDatetime("");
            setEndDatetimeRange("");
            setFilterNew(false);
            setFilterNewContainers(false);
          }}
          variant={ButtonVariants.SECONDARY}
          disabled={loading || allSearchValuesAreEmpty}
        />
      </FiltersContainer>
      {containerHistory && containerHistory.records.length > 0 ? (
        <ContainerHistoryTable containerHistory={containerHistory}>
          <Pagination
            page={page}
            startingIndex={containerHistory.starting_index}
            totalCount={containerHistory.total_count}
            itemsPerPage={containerHistory.records_per_page}
            itemsOnPage={containerHistory.records.length}
            onBackAction={() => {
              setPage(page - 1);
            }}
            onForwardAction={() => {
              setPage(page + 1);
            }}
            disabled={loading}
          />
        </ContainerHistoryTable>
      ) : (
        <NoHistory>No history found for this container</NoHistory>
      )}
    </HistoryContainer>
  );
}
